/**
 * ProductInquiryAnalytics — D2, `/rma/analytics/inquiry-products` (UI2 Teil 10,
 * G13b-2). Ein Bericht über Produktbezüge in Anfragen; KEINE Zeilen, keine
 * Tabelle, kein Seitenwechsel.
 *
 *   GET /api/rma/analytics/inquiry-products?window=&language=&inquiry_type=
 *
 * WAS HIER GILT (Plan `2026-10-21-ui2-t10-g13b-berichte-2.md` §2.2)
 * ----------------------------------------------------------------
 * 1. **Der Zeitraum ist ein RAHMEN, kein Filter** (§3.2, N3.5): er legt fest,
 *    WIE die Kennzahlen gelesen werden, und verbirgt keine Datensätze. Deshalb
 *    `RangeToggle` — und deshalb steht er in `liste-z1` (P14 misst nur die
 *    sechs Zonenmarken; ohne die Marke sähe die Probe das Segment nie).
 * 2. **Die zwei Facetten führen KEIN „All"** (B6/N3.2, P25). Leer heisst alle;
 *    der Server defaultet ohnehin auf `all` (§4.1), deshalb wird ein leerer
 *    Wert NICHT gesendet.
 * 3. **Unbekannt ist nie 0** (P10, W2): fehlt eine Zahl in der Nutzlast, steht
 *    „—" mit dem Grund als `hint`; die dritte Kachel sagt „no resolved cases
 *    yet" statt einer 0, die wie ein Zähler aussähe.
 * 4. **Der Leerzustand ist gemessen, nicht behauptet** (B2): auf prod trägt
 *    keine Anfrage einen Produktbezug (§4.2a). Die Handlung „Show 365 days"
 *    erscheint deshalb NUR, wenn das Fenster kleiner als 365 ist — der Client
 *    kann nicht wissen, ob 365 Tage etwas enthielte, und eine Handlung, die
 *    nichts ändert, ist eine Sackgasse.
 * 5. **Der Zonenvertrag ist Bauanforderung:** `liste-kopf` → `liste-kpi` →
 *    `liste-panel` (mit `liste-z1` und `liste-z3`). Die Reihenfolge prüft P3;
 *    `liste-panel` ist die Messfläche, auf die `oeffne()` wartet.
 *
 * 🔴 **Warum Z1 hier von Hand steht und nicht `ListToolbar` ist:** D2 hat
 * weder Suche noch Zähler noch Zeilen — die Leiste hätte genau zwei Slots.
 * `ListToolbar` blendet Facetten unterhalb `sm` AUS (mobil gehören sie in sein
 * Blatt); §2.2 S2 verlangt für D2 aber „mobil dieselbe Pille". Der Handel ist
 * damit: zwei Zonen selbst gesetzt, dafür die Pille auf beiden Breiten. Die
 * Höhe je Zeile (N2.4/P4) trägt jede der drei Flächen über `FIELD_HEIGHT_SX`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Alert, Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
  ResponsiveContainer, LineChart, Line,
} from 'recharts'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InsightsIcon from '@mui/icons-material/Insights'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import ViewHeader from '../components/ViewHeader'
import KpiRow from '../components/common/KpiRow'
import StatTile from '../components/common/StatTile'
import RangeToggle from '../components/common/RangeToggle'
import BottomSheetSelect from '../components/common/BottomSheetSelect'
import ActiveFilterBar from '../components/common/ActiveFilterBar'
import EmptyState from '../components/common/EmptyState'
import useListQuery from '../components/common/listQuery/useListQuery'
import { FIELD_HEIGHT_SX, FS, SP, V2 } from '../theme/tokens'
import { formatNumber } from '../utils/format'

const API = process.env.REACT_APP_API_BASE_URL

// Colors per inquiry_type — keep in sync with backend's five buckets.
const TYPE_COLORS = {
  product_advisory: V2.infoText, // blue — pre-purchase / advisory
  delivery_status:  V2.warnText, // orange — where-is-my-package
  return_policy:    V2.agent,    // purple — 14-Tage-Widerruf
  invoice:          V2.okText,   // green — Rechnung / VAT
  general:          V2.ink45,    // grey — catch-all
}

const TYPE_LABELS = {
  product_advisory: 'Advisory',
  delivery_status:  'Delivery',
  return_policy:    'Withdrawal',
  invoice:          'Invoice',
  general:          'Other',
}

/** Die vier Zeiträume — Objekte, nicht nackte Zahlen: der Chip in Z3 soll
 *  „Period: 90 days · default" heissen und nicht „Period: 90". */
const PERIOD_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
]

/** Die Werteliste der Sprache — GESCHLOSSEN und ohne `all` (B6/N3.2). */
const LANGUAGE_OPTIONS = [
  { value: 'de', label: 'German' },
  { value: 'en', label: 'English' },
]

/** Die Werteliste der Anfrageart — dieselben fünf Eimer wie der Server. */
const TYPE_OPTIONS = Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }))

/** Truncate a product title to first N chars for x-axis labels, since long
 *  titles overlap horribly on bar charts. */
function shortTitle(t, n = 28) {
  if (!t) return ''
  return t.length <= n ? t : t.slice(0, n - 1) + '…'
}

/** Eine Zahl aus der Nutzlast — `null`, wenn der Server sie NICHT meldet.
 *  🔴 Nicht `?? 0`: „unbekannt" ist nicht „null" (P10, Regel 22 §3). */
function zahl(wert) {
  return typeof wert === 'number' && Number.isFinite(wert) ? wert : null
}

export default function ProductInquiryAnalytics() {
  // 🔴 R16-B1 (Runde 2): der Theme-Modus entscheidet die TEXTFARBE des
  // „Other"-Chips. Die Füllung (`TYPE_COLORS.general`) ist in beiden Modi
  // dieselbe — sie ist die Farbe des Balkens im Diagramm daneben —, aber ihr
  // Untergrund ist es nicht: `Panel` legt `background.paper` darunter, und das
  // ist hell weiß und dunkel das dunkle Papier (`theme.js`). Eine feste
  // Textfarbe kann deshalb nur in EINEM Modus richtig sein. Die Rechnung steht
  // an der Chip-Stelle unten.
  const theme = useTheme()
  // Die Achsen des Hooks. `single` + geschlossene Werteliste, damit ein
  // unbekannter Altwert VERWORFEN wird und der Hinweis erscheint (P13) —
  // und damit `?period=all` als MARKER wirkt (der Hook liefert dafür `null`).
  const dimensionen = useMemo(() => [
    { key: 'period', kind: 'single', label: 'Period', default: '90', values: PERIOD_OPTIONS },
    { key: 'language', kind: 'single', label: 'Language', default: null, values: LANGUAGE_OPTIONS },
    { key: 'type', kind: 'single', label: 'Type', default: null, values: TYPE_OPTIONS },
  ], [])
  const query = useListQuery({ dimensions: dimensionen })
  const { state, set } = query

  const windowDays = Number(state.period) || 90
  const language = state.language ?? null
  const inquiryType = state.type ?? null

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [versuch, setVersuch] = useState(0)

  const erneut = useCallback(() => setVersuch((v) => v + 1), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = { window: windowDays }
    // 🔴 `null` wird NICHT gesendet: der Server defaultet auf `all` (§4.1).
    if (language) params.language = language
    if (inquiryType) params.inquiry_type = inquiryType
    axios.get(`${API}/api/rma/analytics/inquiry-products`, { params })
      .then((res) => {
        if (cancelled) return
        setData(res.data)
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        setError(e?.response?.data?.error || e?.message || 'Failed to load analytics')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [windowDays, language, inquiryType, versuch])

  const kpis = data?.kpis ?? {}
  const topProducts = data?.top_products ?? []
  const trendWeekly = data?.trend_weekly ?? []
  const typeTotals = data?.inquiry_type_totals ?? {}
  const sampleMin = data?.sample_size_threshold ?? 3

  const gesamt = zahl(kpis.total_inquiries_with_refs)
  const einzig = zahl(kpis.unique_products)
  const median = zahl(kpis.median_resolution_days)

  // 🔴 Die Leerlage hängt an einer GEMELDETEN Null, nicht an einer fehlenden
  // Zahl: `null` heisst „der Server sagt es nicht", und dann ist der Bericht
  // nicht leer, sondern unbekannt (P10).
  // 🔴 R16-C6 (Runde 2): `!error` ist hier WEG. Es war die Bedingung, unter der
  // ein gescheiterter NACHladeversuch auf einem leeren Altstand den benannten
  // Leerzustand durch leere Diagramme ersetzte — die Zahl 0 war gemeldet, der
  // Grund stand nirgends mehr. Fehlt `data` ganz, ist `gesamt` `null` (nicht 0),
  // die Fehlerkarte trägt den Fall also weiterhin. Auf prod ist der leere
  // Bericht der NORMALFALL (Plan §4.2a), nicht ein Randfall.
  const leer = !loading && gesamt === 0
  // N8.5: im Fehler des ERSTEN Aufbaus bleibt die Leiste stehen und ist gesperrt.
  const sperre = !loading && Boolean(error) && data == null
  // 🔴 R16-W1 (Runde 1): ein gescheiterter NACHladeversuch darf einen bereits
  // gerenderten Bericht nicht löschen. `data != null` heisst: es gibt einen
  // Stand — er bleibt stehen, und der Fehler erscheint als Hinweis darüber
  // (Lehre 6). Nur beim ERSTEN Abruf ohne Daten gibt es die Fehlerkarte.
  const fehlerkarte = Boolean(error) && data == null
  const hinweis = Boolean(error) && data != null

  // 🔴 R16-C4 (Runde 2): `fenster` ist das GELADENE Fenster (`data.window_days`),
  // nicht die neue Wahl. Nach einem gescheiterten Nachladen stehen deshalb zwei
  // Zeiträume auf dem Schirm — das Segment sagt die WAHL, die Kachel und der
  // Hinweis sagen den STAND. Damit sie sich nicht wortlos widersprechen, nennt
  // der Hinweis unten `fenster` ausdrücklich, und der Leerzustand auch.
  const fenster = zahl(data?.window_days) ?? windowDays
  const zeitraumText = `Period: ${fenster} days`
  // Das GEWÄHLTE Fenster (aus der Adresse) — es entscheidet, ob „Show 365 days"
  // noch etwas ändern kann. 🔴 Nicht `fenster`: nach einem Fehlschlag wäre das
  // der alte Stand, und der Knopf böte ein Fenster an, das schon gewählt ist —
  // ein Klick ohne Wirkung (N8.5).
  const gewaehltesFenster = state.period == null ? windowDays : (Number(state.period) || 90)

  // Shape the stacked bar dataset: one row per SKU with a key per inquiry_type.
  const stackedBarData = topProducts.slice(0, 10).map((p) => ({
    label: shortTitle(p.product_title || p.sku),
    fullTitle: p.product_title,
    sku: p.sku,
    total: p.count,
    warning: p.sample_size_warning,
    ...p.by_inquiry_type,
  }))
  const totalRefsInWindow = topProducts.reduce((summe, p) => summe + (p.count || 0), 0)

  // 🔴 R15-W1 / R16-C2 (Runde 1): ein Durchschnitt aus einem UNBEKANNTEN Nenner
  // ist unbekannt. Vorher rechnete die Unterzeile mit `Math.max(1, einzig ?? 1)`
  // und behauptete bei fehlendem `unique_products` „Avg N refs / product" — eine
  // Zahl, die niemand gemessen hat (Datei-Docstring Punkt 3, Regel 22 §3).
  // Ohne Nenner steht dort deshalb dieselbe Beschreibung wie im Ladezustand.
  const durchschnitt = einzig != null && einzig > 0
    ? `Avg ${formatNumber(totalRefsInWindow / einzig, { digits: 1 })} refs / product`
    : 'across all inquiry types'

  const kpiReihe = (laden) => (
    <KpiRow data-testid="liste-kpi">
      <StatTile
        label="Inquiries with product ref"
        value={laden ? null : gesamt}
        hint={laden ? 'in the selected period' : zeitraumText}
        loading={laden}
        reserveHint
        valueComponent="p"
      />
      <StatTile
        label="Unique products referenced"
        value={laden ? null : einzig}
        hint={laden ? 'across all inquiry types' : durchschnitt}
        loading={laden}
        reserveHint
        valueComponent="p"
      />
      <StatTile
        label="Median resolution"
        value={laden ? null : (median != null ? `${median}d` : null)}
        // 🔴 Die P10-Aussage in Worten: ohne diese Zeile stünde bei fehlendem
        // Median nur „—", und das liest sich wie eine kaputte Zahl.
        hint={laden
          ? 'from case open to resolve'
          : (median != null ? 'from case open to resolve' : 'no resolved cases yet')}
        loading={laden}
        reserveHint
        valueComponent="p"
      />
    </KpiRow>
  )

  return (
    <PageContainer width="wide" boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      <Box data-testid="liste-kopf">
        <ViewHeader
          icon={<InsightsIcon />}
          title="Product-Inquiry Analytics"
          subtitle="Which products are mentioned most often in inquiries — and how the inquiry type is distributed."
        />
      </Box>

      {/* P13 (N10.4): ein verworfener Wert wird GEMELDET, nicht still ersetzt. */}
      {query.ignoredValues.length > 0 && (
        <Alert severity="info" data-testid="ignoriert-hinweis">
          {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
        </Alert>
      )}

      {/* R16-W1: ein gescheiterter Nachladeversuch löscht den geladenen Bericht
          nicht — er steht als Hinweis mit „Try again" darüber, der Stand
          darunter bleibt lesbar.
          🔴 R16-C4 (Runde 2): der Hinweis nennt das GELADENE Fenster. Ohne die
          Zahl standen „Period: 30 days" (Segment, die neue Wahl) und
          „Period: 90 days" (Kachel, der alte Stand) nebeneinander, versöhnt
          allein durch Prosa.
          🔴 R16-C6 (Runde 2): „the last state that loaded", nicht „the report
          below" — war der geladene Stand LEER, steht darunter der Leerzustand
          und kein Bericht; der Satz muss in beiden Fällen wahr sein.
          🔴 R16-W8 (Runde 2, bewusst offen): `${error}` ist der rohe Servertext
          („Request failed with status code 500"). Er bleibt, weil die
          Fehlerkarte des ersten Aufbaus denselben Text zeigt (Runde 1 hat das
          passiert) und „Try again" daneben der nächste Schritt ist. Sauberer
          wäre eine leise zweite Zeile — das ist eine eigene Änderung. */}
      {hinweis && (
        <Alert
          severity="error"
          data-testid="nachlade-hinweis"
          action={(
            <Button color="inherit" size="small" onClick={erneut}>
              Try again
            </Button>
          )}
        >
          {`Refreshing failed — showing the last state that loaded (Period: ${fenster} days). ${error}`}
        </Alert>
      )}

      {/* 🔴 R16-W6 (Runde 2, Orchestrator-Entscheid): die KPI-Reihe bleibt in der
          LEERLAGE stehen — mit den BEKANNTEN Werten (Zähler 0, Median „—"), denn
          der leere Stand ist eine gemeldete Zahl und kein fehlender Wert. Vorher
          fiel die Reihe in Leer- und Fehlerlage ganz weg, und die Seite sprang
          beim Ende des Ladens um ihre Höhe (Foto `390_laden` gegen `390_leer`).
          Die Fehlerkarte des ERSTEN Aufbaus zeigt weiterhin KEINE Reihe: dort
          ist `data` `null`, die Werte sind UNBEKANNT und dürften nicht als 0
          erscheinen (Regel 22 §3). Der verbleibende Sprung Gerüst → Fehlerkarte
          ist der Preis dieser Entscheidung und steht im Bericht. */}
      {loading && kpiReihe(true)}
      {!loading && data != null && kpiReihe(false)}

      <Panel data-testid="liste-panel">
        {/* Z1 — Rahmen-Segment und zwei Facetten-Pillen, EINE Höhe je Zeile. */}
        <Box
          data-testid="liste-z1"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: SP.tight,
            rowGap: SP.tight,
            minWidth: 0,
          }}
        >
          <RangeToggle
            // 🔴 KEIN `?? '90'`: `?period=all` liefert aus dem Hook `null`, und
            // „kein Segment gewählt" ist die zugesagte Form (A16/W10). Ein
            // Rückfall auf die Vorgabe wählte still das 90-Tage-Segment.
            value={state.period}
            onChange={(v) => set('period', v)}
            label="Period"
            options={PERIOD_OPTIONS}
            touchFloor
            disabled={sperre}
            // 🔴 W6: die Höhe ist die der NACHBARN (die Pillen tragen
            // `FACET_PILL_SX`). Ohne diese Zeile stünde das Segment auf MUI
            // `small` und P4 meldete Δ in derselben Zone.
            sx={{ '& .MuiToggleButton-root': { ...FIELD_HEIGHT_SX } }}
          />
          <BottomSheetSelect
            label="Language"
            desktopForm="pill"
            clearable
            value={language ?? ''}
            onChange={(v) => set('language', v || null)}
            options={LANGUAGE_OPTIONS}
            disabled={sperre}
          />
          <BottomSheetSelect
            label="Type"
            desktopForm="pill"
            clearable
            value={inquiryType ?? ''}
            onChange={(v) => set('type', v || null)}
            options={TYPE_OPTIONS}
            disabled={sperre}
          />
        </Box>

        {/* Z3 — die aktive Auswahl samt Vorgabe-Chip und „Clear all".
            🔴 R16-C3 (Runde 1): NUR mit mindestens einem Chip. Bei `?period=all`
            trägt die Abfrage keinen Chip — der Rahmen ist kein Kriterium
            (`useListQuery`, `engtEin`), `state.period` ist `null`.
            ⚠️ **Was der Befund NICHT belegte (nachgemessen in der Nachbesserung,
            2026-10-29):** `ActiveFilterBar` selbst gibt ohne Chip `null` zurück
            (`ActiveFilterBar.jsx:233` — `if (!hatChips && !(mobil && mitZaehler))
            return null`, und `count` übergibt diese Seite nicht). Ein nacktes
            „Filtered by:" stand dort also auch ohne diese Zeile nicht; die
            Bedingung ist ein GÜRTEL an der Aufrufstelle, kein Reparaturstück.
            Der Fehler, den C3 wirklich beschrieb, war der Satz im Leerzustand
            („selected period", obwohl nichts gewählt ist) — der steht unten.
            🔴 R16-W4 (Runde 1): im Fehler des ERSTEN Aufbaus ist Z3
            ausgeblendet, nicht gesperrt — `ActiveFilterBar` hat keinen
            Sperrzustand, und ein offener Chip-✕ hätte einen neuen Abruf
            ausgelöst, während daneben die Leiste gesperrt aussieht (N8.5).
            Ohne Daten gibt es auch keinen Stand, den die Zeile zurückliest. */}
        {query.chips.length > 0 && !sperre && (
          <ActiveFilterBar
            chips={query.chips}
            onClearAll={query.clearAll}
            sx={{ mt: SP.tight }}
          />
        )}

        {fehlerkarte && (
          <EmptyState
            reason="error"
            message="The report could not be loaded."
            detail={error}
            onRetry={erneut}
          />
        )}

        {leer && (
          <EmptyState
            reason="no-data"
            message={fenster >= 365
              ? 'No inquiry in the last 365 days has a product reference.'
              : (state.period == null || hinweis
                // 🔴 R16-C3: `?period=all` ist ein MARKER, es gibt keine
                // „selected period". Der Abruf lief trotzdem über
                // `window={fenster}` — der Satz nennt dieses Fenster.
                // 🔴 R16-C6 (Runde 2): dasselbe gilt nach einem gescheiterten
                // Nachladen. Dann stehen hier die Zahlen des GELADENEN Standes
                // (`fenster`), und „selected period" wäre die neue Wahl — der
                // Satz spräche über ein Fenster, aus dem nichts stammt.
                ? `No inquiry in the last ${fenster} days has a product reference.`
                : 'No inquiry in the selected period has a product reference.')}
            // Der Grund steht zuerst im `message`, die einzige Handlung, die
            // heute etwas bewirken kann, im `detail` (§2.2 S5).
            detail="Product references are recorded by the “Analyze from email” assistant in the /rma/new wizard; cases created there show up in this report."
            // 🔴 Die Handlung hängt am GEWÄHLTEN Fenster, nicht am geladenen:
            // nach einem Fehlschlag ist `fenster` der alte Stand, und ein Knopf
            // auf das bereits gewählte Fenster löste keinen neuen Abruf aus.
            action={gewaehltesFenster >= 365 ? undefined : (
              <Button variant="outlined" size="small" onClick={() => set('period', '365')}>
                Show 365 days
              </Button>
            )}
          />
        )}
      </Panel>

      {/* R16-W2: das Ladegerüst der Diagrammfläche steht in IHRER Form — zwei
          Panels in der Höhe der zwei Diagramme (dieselben 320/220 wie die
          `ResponsiveContainer` darunter), nicht ein leeres weißes Feld.
          🔴 R16-W7 (Runde 2, bewusst offen): das Gerüst hat ZWEI Panels, die
          geladene Seite hat DREI, sobald `inquiry_type_totals` gefüllt ist (die
          Verteilungsfläche dazwischen). Ob sie erscheint, weiss der Client vor
          dem Abruf nicht — er kann die Höhe also nicht reservieren. Der Preis
          ist ein Sprung um ein Panel am Ende des Ladens; ihn zu schliessen
          hiesse raten, und eine dritte Kachel über einem Bericht ohne
          Verteilung wäre eine leere Fläche (dieselbe Sorte Fehler, die W2
          abgestellt hat). */}
      {loading && (
        <Stack spacing={SP.section}>
          {[320, 220].map((hoehe) => (
            <Panel key={hoehe}>
              <Skeleton variant="text" width="35%" />
              <Skeleton variant="rectangular" height={hoehe} sx={{ mt: SP.tight }} />
            </Panel>
          ))}
        </Stack>
      )}

      {!loading && !leer && data != null && (
        <Stack spacing={SP.section}>
          {/* Top-10 Bar Chart: Inquiry-Type Split per Product */}
          <Panel>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" mb={SP.tight}>
              <Typography variant="subtitle2" fontWeight={600}>
                Top 10 products — by inquiry type
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Height = number of inquiries · color = inquiry_type
              </Typography>
            </Stack>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={stackedBarData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: FS.xs }}
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: FS.xs }} />
                <ReTooltip
                  formatter={(v, k) => [v, TYPE_LABELS[k] || k]}
                  labelFormatter={(_l, payload) => payload?.[0]?.payload?.fullTitle || _l}
                />
                <Legend
                  formatter={(k) => TYPE_LABELS[k] || k}
                  wrapperStyle={{ fontSize: FS.sm }}
                />
                {Object.keys(TYPE_LABELS).map((k) => (
                  <Bar key={k} dataKey={k} stackId="stack" fill={TYPE_COLORS[k]} />
                ))}
              </BarChart>
            </ResponsiveContainer>

            {stackedBarData.some((d) => d.warning) && (
              <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mt: SP.tight, py: 0.5 }}>
                <Typography variant="caption">
                  Products with fewer than {sampleMin} references aren’t statistically reliable yet —
                  read the bars with caution until more cases are linked.
                </Typography>
              </Alert>
            )}
          </Panel>

          {/* inquiry_type totals — pill-style summary above trend line */}
          {Object.keys(typeTotals).length > 0 && (
            <Panel>
              <Typography variant="subtitle2" fontWeight={600} mb={SP.tight}>
                Inquiry type distribution in period
              </Typography>
              <Stack direction="row" spacing={SP.tight} flexWrap="wrap" useFlexGap>
                {Object.entries(typeTotals)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, n]) => (
                    <Chip
                      key={k}
                      data-testid={`typ-chip-${k}`}
                      label={`${TYPE_LABELS[k] || k}: ${n}`}
                      size="small"
                      sx={{
                        bgcolor: TYPE_COLORS[k],
                        // 🔴 R16-W3 (Runde 1) / R16-B1 (Runde 2): die Füllung
                        // bleibt (sie ist dieselbe wie im Balkendiagramm), die
                        // TEXTFARBE hängt am Modus.
                        //
                        // Die Füllung ist ein halbtransparentes Token
                        // (`ink45`), sie wird also über `background.paper`
                        // gemischt — und das ist hell WEISS, dunkel das dunkle
                        // Papier (`theme.js`). Dieselbe Füllung ergibt deshalb
                        // zwei verschiedene Flächen, und eine feste Textfarbe
                        // kann nur in einem Modus stimmen:
                        //
                        //   hell  : ink45 über Weiß  → helles Grau, Luminanz ≈
                        //           0,307 → Schwarz 7,14:1 ✅ / Weiß 2,94:1 ❌
                        //   dunkel: ink45 über Papier → sehr dunkles Blau,
                        //           Luminanz ≈ 0,015 → Weiß 16,1:1 ✅ /
                        //           Schwarz 1,30:1 ❌
                        //
                        // Deshalb: `general` dunkel nur im Hellmodus, sonst
                        // Weiß. Die vier anderen Füllungen sind DECKEND — ihr
                        // Kontrast hängt am Untergrund also nicht, und Weiß
                        // trägt in beiden Modi: gemessen 5,02–5,70:1 (Jest,
                        // `ProductInquiryAnalytics.nachbesserung2`).
                        // ⚠️ `getContrastText` löst das hier NICHT: MUI rechnet
                        // die Luminanz des Tokens OHNE Alpha. Gemessen
                        // 2026-10-29: `getContrastText` auf `ink45` gibt im
                        // Hellmodus `#fff` zurück — die 2,94:1 blieben. Die
                        // modusabhängige Zeile ist deshalb die Abhilfe.
                        color: (k === 'general' && theme.palette.mode === 'light')
                          ? 'common.black'
                          : 'common.white',
                        fontWeight: 500,
                        fontSize: FS.sm,
                      }}
                    />
                  ))}
              </Stack>
            </Panel>
          )}

          {/* Weekly Trend */}
          <Panel>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" mb={SP.tight}>
              <Typography variant="subtitle2" fontWeight={600}>
                Volume trend (weekly)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Only inquiries with at least one product reference
              </Typography>
            </Stack>
            {trendWeekly.length >= 2 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={trendWeekly} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                  <XAxis dataKey="week_start" tick={{ fontSize: FS.xs }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: FS.xs }} />
                  <ReTooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke={TYPE_COLORS.product_advisory}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Too few data points for a meaningful trend line — at least 2 weeks with
                populated cases are needed.
              </Typography>
            )}
          </Panel>
        </Stack>
      )}
    </PageContainer>
  )
}
